// src/MapComponent.js

import React, { useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

import { DEFAULT_MAP_CENTER } from './config/locations';
import { useComparison } from './context/ComparisonContext';
import './MapComponent.css';

// Fix for default marker icons
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});
L.Marker.prototype.options.icon = DefaultIcon;

let CompareIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

// Custom Marker with hover-to-open popup
const HoverableMarker = ({ prop, hoveredPropertyId, onMarkerClick, isCompared }) => {
    const markerRef = useRef(null);

    useEffect(() => {
        const el = markerRef.current?.getElement();
        if (el) {
            if (isCompared) el.classList.add('marker-compared');
            else el.classList.remove('marker-compared');
        }
    }, [isCompared]);

    return (
        <Marker
            ref={markerRef}
            position={[prop.latitude, prop.longitude]}
            icon={isCompared ? CompareIcon : DefaultIcon}
            opacity={hoveredPropertyId === prop.property_id ? 1.0 : 0.8}
            zIndexOffset={isCompared ? 500 : (hoveredPropertyId === prop.property_id ? 1000 : 0)}
            eventHandlers={{
                click: () => {
                    if (onMarkerClick) onMarkerClick(prop);
                },
                mouseover: () => {
                    if (markerRef.current) {
                        markerRef.current.openPopup();
                    }
                },
                mouseout: () => {
                    if (markerRef.current) {
                        markerRef.current.closePopup();
                    }
                },
            }}
        >
            <Popup>
                <strong>{prop.address}</strong><br />
                <span>${prop.list_price ? prop.list_price.toLocaleString() : 'N/A'}</span><br />
                <span>ROI: {prop.roi_percentage ? prop.roi_percentage.toFixed(1) + '%' : 'N/A'}</span>
            </Popup>
        </Marker>
    );
};

const MapUpdater = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        if (center) {
            map.flyTo(center, map.getZoom(), { duration: 1 });
        }
    }, [center, map]);
    return null;
};

const MapComponent = ({ properties, hoveredPropertyId, onMarkerClick, center }) => {
    const mapCenter = center || DEFAULT_MAP_CENTER;
    const { isInCompare } = useComparison();

    return (
        <div className="map-container" style={{ height: '100%', width: '100%' }}>
            <MapContainer
                center={mapCenter}
                zoom={12}
                style={{ height: '100%', width: '100%' }}
            >
                <MapUpdater center={center} />
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {properties.map(prop => (
                    prop.latitude && prop.longitude ? (
                        <HoverableMarker
                            key={prop.property_id}
                            prop={prop}
                            hoveredPropertyId={hoveredPropertyId}
                            onMarkerClick={onMarkerClick}
                            isCompared={isInCompare(prop.property_id)}
                        />
                    ) : null
                ))}
            </MapContainer>
        </div>
    );
};

export default MapComponent;